import type { DocAsset } from "@/lib/document/assetTypes";
import type { DocBlock, DocNestedBlock } from "@/lib/document-builder/blocks/blockTypes";
import { sortDocBlocks } from "@/lib/document-builder/blocks/blockReorder";
import type { DocFormulaEvaluation } from "@/lib/document-builder/formulas/docFormulaEval";
import { getDocFormulaResult } from "@/lib/document-builder/formulas/docFormulaEval";
import { formatDocFormulaResult } from "@/lib/document-builder/formulas/docFormulaExpr";
import type { DocVariableValues } from "@/lib/document-builder/variables/variableFill";
import {
  checkboxDisplayChecked,
  textFieldDisplayValue,
} from "@/lib/document-builder/variables/variableSubstitution";

function fillPreviewText(value: string | null): string {
  return value ?? "\u00a0";
}

type RenderableBlock = DocBlock | DocNestedBlock;

type Props = {
  block: RenderableBlock;
  assets: DocAsset[];
  variableValues?: DocVariableValues | null;
  formulaEvaluation: DocFormulaEvaluation | null;
};

export function DocumentBlockPreview({
  block,
  assets,
  variableValues,
  formulaEvaluation,
}: Props) {
  switch (block.type) {
    case "heading": {
      const Tag = block.level === 1 ? "h1" : block.level === 2 ? "h2" : "h3";
      return (
        <Tag className="font-semibold text-gray-900 dark:text-zinc-100">
          {block.text || "—"}
        </Tag>
      );
    }
    case "paragraph":
      return (
        <p className="whitespace-pre-wrap text-sm text-gray-800 dark:text-zinc-200">
          {block.text || "—"}
        </p>
      );
    case "rich_text":
      return (
        <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 dark:text-zinc-200">
          {block.body || "—"}
        </pre>
      );
    case "text_field":
    case "number_field":
      return (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-600 dark:text-zinc-400">
            {block.label || (block.type === "number_field" ? "Number field" : "Text field")}
          </p>
          <p className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-100">
            {fillPreviewText(textFieldDisplayValue(block.variable_key, variableValues))}
          </p>
        </div>
      );
    case "freeform_field":
      return (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-600 dark:text-zinc-400">
            {block.label || "Freeform field"}
          </p>
          <p className="min-h-[4rem] whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 px-2 py-1.5 text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-100">
            {fillPreviewText(textFieldDisplayValue(block.variable_key, variableValues))}
          </p>
        </div>
      );
    case "formula_field": {
      const result = getDocFormulaResult(formulaEvaluation, block);
      return (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-600 dark:text-zinc-400">
            {block.label || block.key}
          </p>
          <p className="rounded border border-gray-200 bg-gray-50 px-2 py-1.5 font-mono text-sm text-gray-900 dark:border-zinc-600 dark:bg-zinc-800/60 dark:text-zinc-100">
            {variableValues ? formatDocFormulaResult(result) : `{{${block.key}}}`}
          </p>
        </div>
      );
    }
    case "checkbox": {
      const checked = checkboxDisplayChecked(block, variableValues);
      return (
        <label className="flex items-start gap-2 text-sm text-gray-800 dark:text-zinc-200">
          <span
            className={[
              "mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border",
              checked
                ? "border-indigo-600 bg-indigo-600 text-white"
                : "border-gray-400 dark:border-zinc-500",
            ].join(" ")}
          >
            {checked ? "✓" : ""}
          </span>
          <span>{block.label || "Checkbox"}</span>
        </label>
      );
    }
    case "signature_line":
      return (
        <div className="space-y-3 text-sm">
          <div>
            <p className="text-xs text-gray-500 dark:text-zinc-400">{block.printed_name_label}</p>
            <p className="mt-1 border-b border-gray-400 pb-1 dark:border-zinc-500">
              {block.printed_name_variable_key
                ? fillPreviewText(
                    textFieldDisplayValue(block.printed_name_variable_key, variableValues),
                  )
                : "\u00a0"}
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
            <div>
              <p className="text-xs text-gray-500 dark:text-zinc-400">{block.signature_caption}</p>
              <p className="mt-3 min-h-[2rem] border-b border-gray-400 pb-1 italic dark:border-zinc-500">
                {block.signature_variable_key
                  ? fillPreviewText(
                      textFieldDisplayValue(block.signature_variable_key, variableValues),
                    )
                  : "\u00a0"}
              </p>
            </div>
            {block.date_variable_key ? (
              <div className="sm:min-w-[8rem]">
                <p className="text-xs text-gray-500 dark:text-zinc-400">{block.date_label}</p>
                <p className="mt-3 min-h-[2rem] border-b border-gray-400 pb-1 dark:border-zinc-500">
                  {fillPreviewText(
                    textFieldDisplayValue(block.date_variable_key, variableValues),
                  )}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      );
    case "table":
      return (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse text-sm">
            <tbody>
              {block.rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => {
                    const Tag = block.header_row && ri === 0 ? "th" : "td";
                    return (
                      <Tag
                        key={ci}
                        className="border border-gray-200 px-2 py-1 text-left dark:border-zinc-700"
                      >
                        {cell || "—"}
                      </Tag>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "two_column":
      return (
        <div className="grid gap-4 md:grid-cols-2">
          {(["left", "right"] as const).map((side) => (
            <div key={side} className="space-y-2">
              {sortDocBlocks(block[side]).map((nested) => (
                <div
                  key={nested.id}
                  className="rounded border border-gray-100 p-2 dark:border-zinc-800"
                >
                  <DocumentBlockPreview
                    block={nested as DocBlock}
                    assets={assets}
                    variableValues={variableValues}
                    formulaEvaluation={formulaEvaluation}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      );
    case "component_ref":
      return (
        <p className="text-sm italic text-gray-500 dark:text-zinc-400">
          Component reference (not supported in V1) — {block.component_id}
        </p>
      );
    case "image": {
      const asset = assets.find((a) => a.id === block.asset_id);
      return (
        <div className="space-y-2">
          {asset?.signed_url ? (
            <img
              src={asset.signed_url}
              alt={block.alt || asset.display_name}
              className="max-h-48 rounded border border-gray-200 object-contain dark:border-zinc-700"
            />
          ) : (
            <p className="text-sm text-gray-500 dark:text-zinc-400">
              {block.asset_id ? "Image asset" : "No image selected"}
            </p>
          )}
          {block.caption ? (
            <p className="text-xs text-gray-600 dark:text-zinc-400">{block.caption}</p>
          ) : null}
        </div>
      );
    }
    case "divider":
      return <hr className="border-gray-300 dark:border-zinc-600" />;
    case "page_break":
      return (
        <p className="text-center text-xs uppercase tracking-wide text-gray-400 dark:text-zinc-500">
          Page break
        </p>
      );
    default: {
      const _exhaustive: never = block;
      return _exhaustive;
    }
  }
}
